/**
 * Sorting a group's card list into something easy to pick from: by issuer (Amex, Chase, …)
 * and kind (credit card, debit or bank, other ways to pay). Works from the names alone, or
 * from your Money page cards when the names match.
 */
export type PayKind = "credit" | "debit" | "other";

export interface CardGroup {
  issuer: string;
  cards: { name: string; kind: PayKind }[];
}

const ISSUERS: [RegExp, string][] = [
  [
    /\bamex\b|american express|\bplatinum\b|\bgold card\b|blue (business|cash)/i,
    "American Express",
  ],
  [/chase|sapphire|freedom|\bina\b|\bcsr\b|\bcsp\b/i, "Chase"],
  [/capital ?one|\bsavor\b|venture|quicksilver/i, "Capital One"],
  [/\bbofa\b|bank of america|customized cash|merrill/i, "Bank of America"],
  [/\bciti\b|citibank|double cash|custom cash|costco anywhere/i, "Citi"],
  [/discover/i, "Discover"],
  [/wells ?fargo|active cash|autograph/i, "Wells Fargo"],
  [/apple card|apple cash/i, "Apple"],
  [/\busb\b|u\.?s\.? bank|altitude/i, "U.S. Bank"],
  [/fidelity/i, "Fidelity"],
  [/schwab/i, "Schwab"],
  [/robinhood/i, "Robinhood"],
  [/sofi/i, "SoFi"],
];

const OTHER = /\b(cash|venmo|zelle|paypal|cash ?app|apple pay|google pay|check|gift card|ebt)\b/i;
const DEBIT = /\b(debit|checking|bank account|ach)\b/i;

export function payKind(name: string): PayKind {
  if (DEBIT.test(name)) return "debit";
  if (OTHER.test(name) && !/card/i.test(name.replace(/gift card/i, ""))) return "other";
  return "credit";
}

export function issuerOf(name: string, known?: Record<string, string>): string {
  const k = known?.[name.trim().toLowerCase()];
  if (k) return k;
  for (const [re, issuer] of ISSUERS) if (re.test(name)) return issuer;
  return payKind(name) === "other" ? "Other ways to pay" : "Other cards";
}

/**
 * The card names grouped by issuer, issuers in alphabetical order with "Other cards" and
 * "Other ways to pay" last. `known` maps a lowercased card name to its issuer (from Money).
 */
export function groupCards(names: readonly string[], known?: Record<string, string>): CardGroup[] {
  const map = new Map<string, CardGroup>();
  for (const name of [...new Set(names.map((n) => n.trim()).filter(Boolean))]) {
    const issuer = issuerOf(name, known);
    const g = map.get(issuer) ?? { issuer, cards: [] };
    g.cards.push({ name, kind: payKind(name) });
    map.set(issuer, g);
  }
  const rank = (i: string) => (i === "Other ways to pay" ? 2 : i === "Other cards" ? 1 : 0);
  return [...map.values()]
    .map((g) => ({ ...g, cards: g.cards.sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => rank(a.issuer) - rank(b.issuer) || a.issuer.localeCompare(b.issuer));
}
